import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiChevronLeft, HiChevronRight, HiDocumentText, HiDownload, HiExternalLink, HiX, HiZoomIn, HiZoomOut } from "react-icons/hi";
import { RotateCw, Maximize2 } from "lucide-react";
import UserAvatar from "../ui/UserAvatar";
import { useChat } from "./ChatProvider";
import { fetchAttachmentUrl } from "../../controllers/chatController";
import { formatFileSize, isImage, isPdf, resolveFileUrl, userName } from "./chatUtils";
import { useIsTouch } from "./useMedia";
import { useBackToClose } from "../../utils/backToClose";

const formatSentAt = (value) => new Date(value).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.5;
const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

// Full-screen viewer for chat images and PDFs. `items` is every viewable attachment in the conversation
// ([{ attachment, message }]); ←/→ moves between them. Signed links can expire, so PDFs are opened with a
// freshly fetched link, images refresh theirs if they fail to load, and downloads always fetch a new link.
// Touch: pinch to zoom, drag to pan when zoomed, double-tap to zoom/reset, swipe left/right between files
// and swipe down to close.
// Also used outside chat (the notice board): pass `fetchLinks(attachmentId)` to get fresh { url, downloadUrl } and
// `describe(item)` returning { userId, name, avatarUrl, at } for the top bar.
const SWIPE_NAV = 60;
const SWIPE_CLOSE = 110;
const DOUBLE_TAP_MS = 300;
function AttachmentViewer({ items, startId, onClose, fetchLinks = fetchAttachmentUrl, describe }) {
  useBackToClose(true, onClose);
  const { users, me } = useChat();
  const [index, setIndex] = useState(() => Math.max(0, items.findIndex((i) => i.attachment.id === startId)));
  const [links, setLinks] = useState({}); // attachmentId -> { url, downloadUrl } fetched in this session
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const stageRef = useRef(null);
  const drag = useRef(null);
  const touch = useIsTouch();
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef(0);
  const [swipe, setSwipe] = useState({ x: 0, y: 0 });

  const current = items[index];
  const attachment = current?.attachment;
  const link = (attachment && links[attachment.id]) || attachment;
  const image = attachment && isImage(attachment.mimeType);
  const pdf = attachment && isPdf(attachment.mimeType);

  const refreshLinks = useCallback(async (id) => {
    const fresh = await fetchLinks(id);
    setLinks((prev) => ({ ...prev, [id]: fresh }));
    return fresh;
  }, [fetchLinks]);

  // Reset the view and get a fresh link for PDFs (the iframe can't report an expired link).
  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
    setLoaded(false);
    setFailed(false);
    if (attachment && isPdf(attachment.mimeType) && !links[attachment.id]) {
      refreshLinks(attachment.id).catch(() => setFailed(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment?.id]);

  const go = useCallback((step) => setIndex((i) => (items.length ? (i + step + items.length) % items.length : 0)), [items.length]);
  const zoomBy = useCallback((delta) => {
    setZoom((z) => {
      const next = clampZoom(z + delta);
      if (next === 1) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);
  const resetView = () => {
    setZoom(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  };

  const download = useCallback(async () => {
    if (!attachment) return;
    try {
      const fresh = await refreshLinks(attachment.id);
      // The download link is served with Content-Disposition: attachment, so this saves the file
      // without leaving the app.
      window.location.assign(resolveFileUrl(fresh.downloadUrl));
    } catch {
      window.location.assign(resolveFileUrl(link.downloadUrl || link.url));
    }
  }, [attachment, link, refreshLinks]);

  const openInNewTab = () => window.open(resolveFileUrl(link.url), "_blank", "noopener,noreferrer");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
      else if (image && (e.key === "+" || e.key === "=")) zoomBy(ZOOM_STEP);
      else if (image && e.key === "-") zoomBy(-ZOOM_STEP);
      else if (image && e.key === "0") resetView();
    };
    window.addEventListener("keydown", onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [go, onClose, zoomBy, image]);

  // Mouse-wheel zoom (needs a non-passive listener to stop the page from scrolling).
  useEffect(() => {
    const el = stageRef.current;
    if (!el || !image) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? ZOOM_STEP / 2 : -ZOOM_STEP / 2);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [image, zoomBy, index]);

  if (!current) return null;

  const owner = describe
    ? describe(current)
    : {
        userId: current.message.senderId,
        name: current.message.senderId === me?.id ? "You" : userName(users[current.message.senderId]),
        avatarUrl: users[current.message.senderId]?.avatarThumbUrl,
        at: current.message.createdAt,
      };

  const onPointerDown = (e) => {
    if (e.pointerType === "touch" || zoom <= 1) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
  };
  const onPointerMove = (e) => {
    if (drag.current) setOffset({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const endDrag = () => {
    drag.current = null;
  };

  // ---- Touch gestures on the stage ----
  const pinchDistance = () => {
    const [a, b] = [...pointers.current.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };
  const startPan = (x, y) => {
    gesture.current = { type: "pan", x, y, zoom, offset };
  };
  const onTouchDown = (e) => {
    if (e.pointerType !== "touch" || e.target.closest("button, a")) return; // let buttons get their taps
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && image) gesture.current = { type: "pinch", dist: pinchDistance(), zoom };
    else if (pointers.current.size === 1) startPan(e.clientX, e.clientY);
  };
  const onTouchMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    if (g.type === "pinch" && pointers.current.size >= 2) {
      const next = clampZoom(g.zoom * (pinchDistance() / g.dist));
      setZoom(next);
      if (next <= 1) setOffset({ x: 0, y: 0 });
    } else if (g.type === "pan") {
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      if (g.zoom > 1) setOffset({ x: g.offset.x + dx, y: g.offset.y + dy });
      else setSwipe({ x: dx, y: Math.max(0, dy) });
    }
  };
  const onTouchUp = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    const last = pointers.current.get(e.pointerId);
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (pointers.current.size === 1 && g?.type === "pinch") {
      // One finger lifted mid-pinch: carry on panning with the other.
      const [rest] = [...pointers.current.values()];
      gesture.current = { type: "pan", x: rest.x, y: rest.y, zoom, offset };
      return;
    }
    if (pointers.current.size > 0) return;
    gesture.current = null;
    if (g?.type === "pan" && g.zoom <= 1) {
      const dx = last.x - g.x;
      const dy = last.y - g.y;
      if (items.length > 1 && Math.abs(dx) > SWIPE_NAV && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
      else if (dy > SWIPE_CLOSE && dy > Math.abs(dx)) onClose();
      else if (image && Math.hypot(dx, dy) < 10) {
        if (Date.now() - lastTap.current < DOUBLE_TAP_MS) {
          lastTap.current = 0;
          setZoom(2);
        } else lastTap.current = Date.now();
      }
    } else if (g?.type === "pan" && image && Math.hypot(last.x - g.x, last.y - g.y) < 10) {
      if (Date.now() - lastTap.current < DOUBLE_TAP_MS) {
        lastTap.current = 0;
        resetView();
      } else lastTap.current = Date.now();
    }
    setSwipe({ x: 0, y: 0 });
  };
  const touchGesturing = Boolean(gesture.current);

  const toolbarButton = "p-2 rounded-lg text-white/85 hover:text-white hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-transparent";

  return createPortal(
    <div className="fixed inset-0 z-[95] flex flex-col bg-neutral-950/[0.97] backdrop-blur-sm text-white" role="dialog" aria-modal="true" aria-label={`Viewing ${attachment.fileName}`} data-chat-viewer>
      {/* Top bar */}
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] bg-neutral-950 border-b border-white/10">
        <UserAvatar name={owner.name} src={owner.avatarUrl} seed={owner.userId} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold" title={attachment.fileName}>{attachment.fileName}</p>
          <p className="truncate text-xs text-white/60">
            {owner.name} · {formatSentAt(owner.at)} · {formatFileSize(attachment.size)}
            {items.length > 1 && ` · ${index + 1} of ${items.length}`}
          </p>
        </div>
        {image && (
          <>
            {!touch && (
              <>
                <button type="button" className={toolbarButton} onClick={() => zoomBy(-ZOOM_STEP)} disabled={zoom <= MIN_ZOOM} title="Zoom out (−)" aria-label="Zoom out">
                  <HiZoomOut className="w-5 h-5" />
                </button>
                <span className="w-12 text-center text-xs tabular-nums text-white/70" aria-live="polite">{Math.round(zoom * 100)}%</span>
                <button type="button" className={toolbarButton} onClick={() => zoomBy(ZOOM_STEP)} disabled={zoom >= MAX_ZOOM} title="Zoom in (+)" aria-label="Zoom in">
                  <HiZoomIn className="w-5 h-5" />
                </button>
              </>
            )}
            <button type="button" className={toolbarButton} onClick={() => setRotation((r) => (r + 90) % 360)} title="Rotate" aria-label="Rotate">
              <RotateCw className="w-5 h-5" />
            </button>
            {!touch && (
              <button type="button" className={toolbarButton} onClick={resetView} title="Reset (0)" aria-label="Reset view">
                <Maximize2 className="w-5 h-5" />
              </button>
            )}
            <span className="mx-1 hidden h-6 w-px bg-white/20 sm:block" />
          </>
        )}
        <button type="button" className={toolbarButton} onClick={openInNewTab} title="Open in new tab" aria-label="Open in new tab">
          <HiExternalLink className="w-5 h-5" />
        </button>
        <button type="button" onClick={download} className="inline-flex items-center gap-1.5 rounded-lg bg-white/15 px-2.5 sm:px-3 py-2 text-sm font-medium hover:bg-white/25" aria-label={`Download ${attachment.fileName}`}>
          <HiDownload className="w-5 h-5" /> <span className="hidden sm:inline">Download</span>
        </button>
        <button type="button" className={toolbarButton} onClick={onClose} title="Close (Esc)" aria-label="Close viewer">
          <HiX className="w-6 h-6" />
        </button>
      </div>

      {/* Stage */}
      <div
        className="relative flex-1 min-h-0 flex items-center justify-center overflow-hidden"
        ref={stageRef}
        style={touch ? { touchAction: "none" } : undefined}
        onPointerDown={touch ? onTouchDown : undefined}
        onPointerMove={touch ? onTouchMove : undefined}
        onPointerUp={touch ? onTouchUp : undefined}
        onPointerCancel={touch ? onTouchUp : undefined}
        data-chat-viewer-stage
      >
        {image && (
          <>
            {!loaded && !failed && <div className="absolute h-10 w-10 animate-spin rounded-full border-4 border-white/20 border-t-white" aria-hidden="true" />}
            <img
              key={attachment.id}
              src={resolveFileUrl(link.url)}
              alt={attachment.fileName}
              draggable={false}
              onLoad={() => setLoaded(true)}
              onError={() => {
                if (!links[attachment.id]) refreshLinks(attachment.id).catch(() => setFailed(true));
                else setFailed(true);
              }}
              onDoubleClick={() => !touch && (zoom > 1 ? resetView() : setZoom(2))}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              style={{
                transform: `translate(${offset.x + swipe.x}px, ${offset.y + swipe.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transition: drag.current || touchGesturing ? "none" : "transform 120ms ease-out",
                opacity: swipe.y ? Math.max(0.4, 1 - swipe.y / 400) : undefined,
                cursor: zoom > 1 ? (drag.current ? "grabbing" : "grab") : "zoom-in",
              }}
              className={`max-h-full max-w-full select-none object-contain ${loaded ? "opacity-100" : "opacity-0"}`}
            />
          </>
        )}
        {pdf && touch && !failed && (
          <div className="flex flex-col items-center gap-3 px-6 text-center" data-chat-viewer-pdf-card>
            <span className="flex h-20 w-16 flex-col items-center justify-center rounded-lg bg-red-600/80 text-xs font-bold">
              <HiDocumentText className="w-8 h-8" />
              PDF
            </span>
            <p className="max-w-xs break-words text-sm font-medium">{attachment.fileName}</p>
            <div className="flex gap-2">
              <button type="button" onClick={openInNewTab} className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-neutral-900">Open</button>
              <button type="button" onClick={download} className="rounded-lg bg-white/15 px-4 py-2 text-sm font-medium">Download</button>
            </div>
          </div>
        )}
        {pdf && !touch && (
          links[attachment.id] ? (
            <iframe
              key={attachment.id}
              title={attachment.fileName}
              src={`${resolveFileUrl(links[attachment.id].url)}#view=FitH`}
              className="h-full w-full max-w-5xl bg-white"
            />
          ) : (
            !failed && <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/20 border-t-white" aria-hidden="true" />
          )
        )}
        {failed && (
          <div className="flex flex-col items-center gap-3 text-center">
            <HiDocumentText className="w-14 h-14 text-white/50" />
            <p className="text-sm text-white/80">This file couldn&apos;t be shown here.</p>
            <button type="button" onClick={download} className="rounded-lg bg-white/15 px-4 py-2 text-sm font-medium hover:bg-white/25">Download it instead</button>
          </div>
        )}

        {items.length > 1 && !touch && (
          <>
            <button type="button" onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-3 hover:bg-black/70" aria-label="Previous attachment">
              <HiChevronLeft className="w-6 h-6" />
            </button>
            <button type="button" onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-3 hover:bg-black/70" aria-label="Next attachment">
              <HiChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {items.length > 1 && (
        <div className="flex justify-center gap-2 overflow-x-auto px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-neutral-950 border-t border-white/10" aria-label="All images and PDFs in this chat">
          {items.map((item, i) => (
            <button
              key={item.attachment.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Show ${item.attachment.fileName}`}
              aria-current={i === index}
              className={`h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 ${i === index ? "border-white" : "border-transparent opacity-60 hover:opacity-100"}`}
            >
              {isImage(item.attachment.mimeType) ? (
                <img src={resolveFileUrl((links[item.attachment.id] || item.attachment).url)} alt="" className="h-full w-full object-cover" loading="lazy" />
              ) : (
                <span className="flex h-full w-full flex-col items-center justify-center bg-red-600/80 text-[10px] font-bold">
                  <HiDocumentText className="w-6 h-6" />
                  PDF
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}

export default AttachmentViewer;
